// src/components/SongsBrowser/SongsBrowser.jsx
import React, { useEffect, useRef, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { motion, useReducedMotion } from "framer-motion";
import SectionWrapper from "../SectionWrapper/SectionWrapper";
import songsJson from "../../data/songs.json";
import ohjelmistoImage from "../../assets/esiintymiset_image.webp";

const categories = {
  kaikki: "Kaikki",
  kotimainen: "Kotimainen",
  ulkomainen: "Ulkomainen",
  tanssimusiikki: "Tanssimusiikki"
};

// Yhdistetään kategoriat vain näkymää varten. JSON-tiedosto ei muutu.
const songLists = songsJson.ohjelmisto;
const allSongs = [
  ...songLists.kotimainen,
  ...songLists.ulkomainen,
  ...songLists.tanssimusiikki
];

const ga = (event, data = {}) => {
  if (
    typeof window !== "undefined" &&
    typeof window.gaEvent === "function"
  ) {
    window.gaEvent(event, data);
  }
};

export default function SongsBrowser() {
  const [activeCategory, setActiveCategory] = useState("kaikki");
  const [searchTerm, setSearchTerm] = useState("");
  const [showList, setShowList] = useState(true);

  const headingRef = useRef(null);
  const listRef = useRef(null);
  const viewTracked = useRef(false);
  const reduceMotion = useReducedMotion();

  const query = searchTerm.trim();
  const normalizedQuery = query.toLowerCase();

  const selectedSongs =
    activeCategory === "kaikki"
      ? allSongs
      : songLists[activeCategory];

  const filteredSongs = selectedSongs.filter(
    ({ title, artist }) =>
      title.toLowerCase().includes(normalizedQuery) ||
      artist.toLowerCase().includes(normalizedQuery)
  );

  useEffect(() => {
    AOS.init({ once: true, duration: 650 });

    if (
      !headingRef.current ||
      typeof window.IntersectionObserver !== "function"
    ) {
      return;
    }

    // Pelkkä komponentin latautuminen ei vielä ole osion katselu.
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (
          !entry?.isIntersecting ||
          entry.intersectionRatio < 0.5 ||
          viewTracked.current
        ) {
          return;
        }

        viewTracked.current = true;
        ga("ohjelmisto_nakyvissa");
        observer.disconnect();
      },
      { threshold: 0.5 }
    );

    observer.observe(headingRef.current);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!query) return;

    // Uusi kirjain peruu edellisen ajastimen.
    // Hakutapahtuma lähtee vasta kirjoitustauon jälkeen.
    const timer = window.setTimeout(() => {
      ga("ohjelmisto_haku", {
        haku: query,
        kategoria: activeCategory
      });
    }, 500);

    return () => window.clearTimeout(timer);
  }, [query, activeCategory]);

  useEffect(() => {
    // Uudet tulokset näytetään listan alusta.
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }

    // Listan muuttuminen vaikuttaa alempien osioiden sijaintiin.
    AOS.refresh();
  }, [activeCategory, normalizedQuery, showList, reduceMotion]);

  const changeCategory = (category) => {
    setActiveCategory(category);
    setShowList(true);

    if (category !== activeCategory) {
      ga("ohjelmisto_kategoria_vaihdettu", {
        kategoria: category
      });
    }
  };

  const toggleList = () => {
    const next = !showList;

    setShowList(next);

    ga("ohjelmisto_lista_toggle", {
      tila: next ? "auki" : "kiinni"
    });
  };

  return (
    <SectionWrapper id="ohjelmisto">
      <div
        role="region"
        aria-labelledby="ohjelmisto-heading"
        className="relative overflow-hidden rounded-[2rem] border border-black/5 bg-white/70 p-3 shadow-xl backdrop-blur-sm sm:p-5 md:p-8 dark:border-white/10 dark:bg-white/5"
      >
        <div className="pointer-events-none absolute -left-24 top-10 h-64 w-64 rounded-full bg-joyrideCyan/15 blur-3xl" />
        <div className="pointer-events-none absolute -right-24 bottom-10 h-72 w-72 rounded-full bg-joyridePurple/20 blur-3xl" />

        <div className="relative z-10 grid items-start gap-8 lg:grid-cols-[0.85fr_1.15fr]">
          {/* Sisältö ensin mobiilissa, työpöydällä oikealla. */}
          <div
            className="min-w-0 lg:order-2"
            data-aos={reduceMotion ? undefined : "fade-up"}
          >
            <p className="mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-joyridePurple dark:text-joyrideCyan">
              Biisit ja tunnelma
            </p>

            <h2
              ref={headingRef}
              id="ohjelmisto-heading"
              className="mb-4 font-lobster text-3xl font-bold text-joyridePurple sm:text-4xl md:text-5xl dark:text-joyrideCyan dark:text-shadow-lg"
            >
              Bilebändi Joyriden ohjelmisto
            </h2>

            <p className="mb-6 text-base leading-relaxed text-joyrideNavy/85 md:text-lg dark:text-joyrideLight/85">
              Kotimaisia hittejä, kansainvälisiä bileklassikoita ja
              perinteistä tanssimusiikkia. Hae kappaleen tai artistin
              nimellä ja rajaa halutessasi kategoriaa. YouTube-linkeistä
              voit kuunnella kappaleita.
            </p>

            <div
              role="group"
              aria-label="Ohjelmiston kategoriat"
              className="mb-5 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:gap-3"
            >
              {Object.entries(categories).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={activeCategory === value}
                  onClick={() => changeCategory(value)}
                  className={`min-h-[44px] rounded-full border px-3 py-2 text-sm font-bold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-joyridePurple focus-visible:ring-offset-2 dark:focus-visible:ring-joyrideCyan dark:focus-visible:ring-offset-joyrideNavy ${
                    activeCategory === value
                      ? "border-transparent bg-joyrideCyan text-joyrideNavy shadow-[0_0_22px_rgba(0,240,255,0.35)]"
                      : "border-joyridePurple/20 bg-white/80 text-joyrideNavy shadow-sm hover:border-joyrideCyan hover:text-joyridePurple dark:border-white/10 dark:bg-white/10 dark:text-joyrideLight dark:hover:text-joyrideCyan"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            <label
              htmlFor="ohjelmisto-search"
              className="mb-2 block text-sm font-semibold text-joyrideNavy dark:text-joyrideLight"
            >
              Hae kappaletta tai artistia
            </label>

            <div className="flex flex-col gap-3 sm:flex-row">
              <input
                id="ohjelmisto-search"
                type="search"
                autoComplete="off"
                spellCheck={false}
                placeholder="Kirjoita nimi…"
                value={searchTerm}
                onChange={(event) => {
                  setSearchTerm(event.target.value);
                  setShowList(true);
                }}
                className="min-h-[44px] min-w-0 flex-1 rounded-2xl border border-joyridePurple/20 bg-white/90 px-4 py-2 text-base text-joyrideNavy shadow-sm outline-none transition focus:border-joyridePurple focus:ring-2 focus:ring-joyridePurple/30 dark:border-white/10 dark:bg-white/10 dark:text-joyrideLight dark:placeholder:text-joyrideLight/60 dark:focus:border-joyrideCyan dark:focus:ring-joyrideCyan/30"
              />

              <button
                type="button"
                onClick={toggleList}
                aria-expanded={showList}
                aria-controls="ohjelmisto-list"
                className="min-h-[44px] shrink-0 rounded-2xl bg-joyridePurple px-5 py-2 text-sm font-bold text-white shadow-lg transition hover:bg-joyrideCyan hover:text-joyrideNavy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-joyridePurple focus-visible:ring-offset-2 dark:bg-joyrideCyan dark:text-joyrideNavy dark:focus-visible:ring-joyrideCyan dark:focus-visible:ring-offset-joyrideNavy"
              >
                {showList ? "Piilota lista" : "Näytä lista"}
              </button>
            </div>

            {/* Tulosmäärä ilmoitetaan myös ruudunlukijalle. */}
            <p
              role="status"
              aria-atomic="true"
              className="mb-3 mt-3 text-sm text-joyrideNavy/80 dark:text-joyrideLight/80"
            >
              {categories[activeCategory]}: {filteredSongs.length}{" "}
              {filteredSongs.length === 1 ? "kappale" : "kappaletta"}
            </p>

            {/* Hakuteksti ei pakota listaa näkyviin.
                Suljetun listan linkkeihin ei voi siirtyä tabulaattorilla. */}
            <div id="ohjelmisto-list" hidden={!showList}>
              {showList && (
                <motion.div
                  ref={listRef}
                  initial={reduceMotion ? false : { opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: reduceMotion ? 0 : 0.2 }}
                  role="region"
                  aria-label="Ohjelmiston kappaleet"
                  tabIndex={0}
                  className="max-h-80 overflow-y-auto overscroll-contain rounded-3xl border border-joyrideNavy/10 bg-white/70 p-2 shadow-inner backdrop-blur-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-joyridePurple sm:p-3 dark:border-white/10 dark:bg-white/5 dark:focus-visible:ring-joyrideCyan"
                >
                  <ul className="space-y-2">
                    {filteredSongs.length > 0 ? (
                      filteredSongs.map((song, index) => (
                        <li
                          key={`${song.title}-${song.artist}-${index}`}
                          className="rounded-2xl border border-transparent bg-white/60 p-3 transition hover:border-joyrideCyan/40 hover:bg-joyrideCyan/10 sm:p-4 dark:bg-white/5"
                        >
                          <strong className="block break-words text-joyridePurple dark:text-joyrideCyan">
                            {song.title}
                          </strong>

                          <span className="mt-1 block break-words text-sm text-joyrideNavy/80 dark:text-joyrideLight/80">
                            {song.artist}
                          </span>

                          <a
                            href={song.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`Kuuntele ${song.title} – ${song.artist} YouTubessa (avautuu uuteen välilehteen)`}
                            onClick={() =>
                              ga("ohjelmisto_youtube_klikattu", {
                                kappale: song.title
                              })
                            }
                            className="mt-1 inline-flex min-h-[44px] items-center rounded px-1 text-xs font-semibold uppercase tracking-[0.1em] text-joyridePurple underline underline-offset-4 transition hover:text-joyrideNavy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-joyridePurple dark:text-joyrideCyan dark:hover:text-joyrideLight dark:focus-visible:ring-joyrideCyan"
                          >
                            Kuuntele YouTubessa
                          </a>
                        </li>
                      ))
                    ) : (
                      <li className="rounded-2xl bg-white/60 p-4 text-center text-joyrideNavy dark:bg-white/5 dark:text-joyrideLight">
                        Ei osumia. Kokeile toista hakusanaa
                        {activeCategory !== "kaikki"
                          ? " tai valitse Kaikki-kategoria."
                          : "."}
                      </li>
                    )}
                  </ul>
                </motion.div>
              )}
            </div>
          </div>

          {/* Kuva säilyy työpöydällä vasemmalla, mobiilissa listan jälkeen. */}
          <div
            className="relative min-w-0 lg:order-1"
            data-aos={reduceMotion ? undefined : "fade-up"}
          >
            <div className="pointer-events-none absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-joyrideCyan/20 via-transparent to-joyridePurple/25 blur-2xl" />

            <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-white p-2 shadow-2xl dark:bg-white/10">
              <img
                src={ohjelmistoImage}
                alt="Bilebändi Joyriden ohjelmisto ja kappalelista"
                className="h-56 w-full rounded-[1.5rem] object-cover sm:h-72 md:h-[420px]"
                loading="lazy"
                width="900"
                height="700"
              />

              <div className="absolute bottom-5 left-5 rounded-full bg-joyrideNavy/90 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-joyrideCyan shadow-lg backdrop-blur-sm dark:bg-black/70">
                Ohjelmisto livenä
              </div>
            </div>
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
}